import { CircularProgress } from "@mui/material";
import { Button, ButtonPlanilha } from "components/Button";
import { useRef, useState, useEffect } from "react";
import { useDebaunce } from "utils/debaunce";
import PlusIcon from "assets/iconPlusButtonAdd.svg";
import PlanilhaIcon from "assets/planilhaIcon.svg";
import PDFIcon from "assets/pdfIcon.svg";
import { ToolbarWrapper } from "./styles";
import { toast } from "react-toastify";
import { deliveryService } from "services/deliveryService";
import { FilterDeliveryStatus } from "components/FilterDeliveries";
import { FilterDrivers } from "components/FilterDrivers";
import { Calendar } from "components/Calendar";
import { FilterDeliveries } from "components/FilterDeliveriesPage";
import InputFilter from "components/Toolbar/InputFilter";
import ButtonFilter from "./ButtonFilter";
import { FilterBoxesReturn } from "components/FilterBoxesReturn";
import { ICheckboxOption } from "components/checkbox";
import { FilterRoutes } from "components/FilterRoutes";
import { FilterPunctuality } from "components/FilterPunctuality";
import { FilterComplaints } from "components/FilterComplaints";
import { FilterCostManagement } from "components/FilterCostManagement";
import { useSearchParams } from "react-router-dom";
import { NewButton } from "components/NewButton";
import { defaultTheme } from "styles/theme/default";
import { dateToday } from "utils/dateToday";

type ToolbarButton = {
  type: "filled" | "outlined";
  text: string;
  onClick: (x?: any, y?: any) => any;
  disableState?: boolean;
  customWidth?: string;
  icon?: JSX.Element;
  customMargin?: string;
};

interface ToolbarProps {
  params?: any;
  onChangeSearch?: (value: string) => void;
  minSearchSize?: number;
  paramsDate?: {};
  paramsDateState?: any;
  paramsTeam?: {};
  paramsTeamState?: any;
  paramState?: any;
  loadingState: boolean;
  placeholder?: string;
  add?: boolean;
  genericAdd?: boolean;
  addText?: string;
  export?: boolean;
  import?: boolean;
  onAdd?: () => void;
  onDownload?: () => void;
  onImport?: () => void;
  page?: string;
  extraButtons?: ToolbarButton[];
  hideFilter?: boolean;
  hideSearch?: boolean;
  alignButtonsToEnd?: boolean;
  checkboxes?: ICheckboxOption[];
  addDisabled?: boolean;
}

export function Toolbar(props: ToolbarProps) {

  const debaunceFilter = useDebaunce({ fn: onChangeParams, delay: 500 });
  const [params, setParams] = [props.params, props.paramState];
  const [paramsDate, setParamsDate] = [props.paramsDate, props.paramsDateState];
  const [paramsTeam, setParamsTeam] = [props.paramsTeam, props.paramsTeamState];
  const loading = props.loadingState;
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [filterSelect, setFilterSelect] = useState(false);
  const [searchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState(searchParams.get("search") || "");

  useEffect(() => {
    const searchValue = searchParams.get("search");
    if (searchValue) {
      setInputValue(searchValue);
      onChangeParams({ search: searchValue, page: 1 });
    }
  }, []);

  // Sincroniza inputValue com props.params.search
  useEffect(() => {
    const currentSearch = props.params?.search || "";
    setInputValue(currentSearch);
  }, [props.params?.search]);

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files) {
      handleSubmit(event.target.files[0]);
    }
  };

  const handleSubmit = async (file: File) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("arquivo", file);
      await deliveryService.uploadRouteFile(formData);
      toast.success("Arquivo enviado com sucesso!");
    } catch (error) {
      toast.error(
        "Ocorreu um erro ao enviar o arquivo. Por favor, tente novamente mais tarde."
      );
    } finally {
      setIsLoading(false);
    }
  };

  function onChangeParams(newValues: any) {
    if (props.loadingState) return;
    setParams({ ...params, ...newValues });
  }

  function onChangeParamsDate(params: any) {
    setParamsDate((state: any) => ({ ...state, ...params }));
  }

  function onChangeParamsTeam(params: any) {
    setParamsTeam((state: any) => ({ ...state, ...params }));
  }

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const getFilterComponent = (page: string) => {
    const defaultParams = {
      setOpen: setFilterSelect,
      open: filterSelect,
    };

    switch (page) {
      case "deliveryStatus":
        return (
          <FilterDeliveryStatus
            onChangeParamsDate={onChangeParamsDate}
            onChangeParams={onChangeParams}
            {...defaultParams}
          />
        );
      case "Drivers":
        return (
          <FilterDrivers onChangeParams={onChangeParams} {...defaultParams} />
        );
      case "SupportDriver":
      case "ExportRoutes":
        return (
          <div className="calendarContainer">
            <Calendar
              dateRangeLimit={1}
              position="below"
              {...defaultParams}
              initialStartDate={params?.deliveryDate}
              onApplyFilter={(startDate, endDate) => {
                onChangeParams({
                  "deliveryDate": startDate
                });
              }}
              onClearFilter={() => {
                onChangeParams({
                  "deliveryDate": dateToday()
                });
              }}
            />
          </div>
        );
      case "BoxReturn":
        return (
          <FilterBoxesReturn
            checkboxes={props.checkboxes}
            onChangeParams={onChangeParams}
            {...defaultParams}
            loading={loading}
            initialParams={props.params}
          />
        );
      case "BoxCount":
        return (
          <div className="calendarContainer">
            <Calendar
              dateRangeLimit={14}
              position="below"
              {...defaultParams}
              onApplyFilter={(startDate, endDate) => {
                onChangeParams({
                  "startDeliveryDate": startDate,
                  "endDeliveryDate": endDate
                });
              }}
              onClearFilter={() => {
                onChangeParams({
                  "startDeliveryDate": dateToday(),
                  "endDeliveryDate": dateToday()
                });
              }}
            />
          </div>
        );
      case "Routes":
        return (
          <FilterRoutes
            checkboxes={props.checkboxes}
            onChangeParamsTeam={onChangeParamsTeam}
            {...defaultParams}
          />
        );
      case "Deliveries":
        return (
          <FilterDeliveries
            onChangeParamsDate={onChangeParamsDate}
            onChangeParams={onChangeParams}
            {...defaultParams}
          />
        );
      case "punctuality":
        return (
          <FilterPunctuality
            open={filterSelect}
            setOpen={setFilterSelect}
            onChangeParams={onChangeParams}
            loading={loading}
            initialParams={props.params}
          />
        );
      case "complaints":
        return (
          <FilterComplaints
            open={filterSelect}
            setOpen={setFilterSelect}
            onChangeParams={onChangeParams}
            loading={loading}
            initialParams={props.params}
          />
        );
      case "first-mile-costs":
      case "last-mile-costs":
      case "support-costs":
        return (
          <FilterCostManagement
            onChangeParams={onChangeParams}
            loading={loading}
            initialParams={props.params}
            {...defaultParams}
          />
        );
    }
  };

  const onChangeInputFilter = (value: string) => {
    setInputValue(value);

    if (value === "" || value.length >= (props.minSearchSize || 0)) {
      debaunceFilter({ ...params, search: value, page: 1 });
    }
  };

  const onClickFilter = () => {
    if (props.page === "TeamPage") {
      toast.warn("Função ainda em desenvolvimento.");
      return;
    }

    setFilterSelect(true);
  };

  const onChangeInputFilterCustom = (value: string) => {
    setInputValue(value);
    props.onChangeSearch?.(value);
  };

  return (
    <ToolbarWrapper className="toolbar" alignButtonsToEnd={!!props.alignButtonsToEnd} hideFilter={props.hideFilter}>
      <div className="searchAndFilter">
        <div className="searchContainer">
          {!props.hideSearch &&
            <InputFilter
              onChange={props.onChangeSearch ? onChangeInputFilterCustom : onChangeInputFilter}
              placeholder={props.placeholder as string}
              value={inputValue}
            />
          }
        </div>
        {!props.hideFilter && <ButtonFilter onClickFilter={onClickFilter} />}
        <div className="filterContainer">
          {getFilterComponent(props.page as string)}
        </div>
      </div>
      <div className="buttons">
        {props.add && (
          <div className="buttonContainer">
            <NewButton
              variantType="filled"
              onClick={props.onAdd}
              disabled={props.addDisabled}
              sx={{
                minWidth: props.addText ? 154 : 134,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap',
                height: '40px',
                fontWeight: '600',
              }}
              startIcon={!props.genericAdd && <img className="plusIcon" src={PlusIcon} alt="" />}
            >
              {props.addText || "Adicionar"}
            </NewButton>
          </div>
        )}
        {props.extraButtons &&
          props.extraButtons?.map((button, index) => (
            <div className="buttonContainer">
              <NewButton
                key={index}
                variantType={button.type}
                onClick={button.onClick}
                disabled={button.disableState}
                sx={{
                  minWidth: button.customWidth ? ['100%', button.customWidth] : ['100%', 134],
                  margin: button.customMargin,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  whiteSpace: 'nowrap',
                  height: '40px',
                  fontWeight: '600',
                }}
                startIcon={button.icon}
              >
                {button.text}
              </NewButton>
            </div>
          ))}

        {props.import && (
          <NewButton
            variantType="outlined"
            onClick={handleButtonClick}
            sx={{
              minWidth: ['100%', 220],
              display: 'flex',
              alignItems: 'center',
              marginTop: ['20px', '0'],
              gap: '12px',
              height: '40px',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              color: `${defaultTheme.colors.base}`,
            }}
            startIcon={<img className="planilhaIcon" src={PlanilhaIcon} alt="" />}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Importar planilha"
            )}
            <input
              name="file-input"
              ref={fileInputRef}
              type="file"
              onChange={handleFileInputChange}
              accept=".xlsx, .xlsm"
              hidden
            />
          </NewButton>
        )}
        {props.export && (
          <NewButton
            variantType="outlined"
            onClick={props.onDownload}
            sx={{
              minWidth: ['100%', 200],
              alignItems: 'center',
              gap: '12px',
              height: '40px',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              display: ['none', 'flex']
            }}
            startIcon={<img className="planilhaIcon" src={PlanilhaIcon} alt="" />}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Baixar planilha"
            )}
          </NewButton>
        )}
        {props.page === "Routes" && (
          <NewButton
            variantType="outlined"
            onClick={props.onDownload}
            sx={{
              minWidth: ['100%', 190],
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              height: '40px',
              fontWeight: '600',
              marginTop: ['20px', '0']
            }}
            startIcon={<img className="planilhaIcon" src={PDFIcon} alt="" />}
          >
            {loading ? (<CircularProgress size={24} color="inherit" />) : ("Baixar PDF")}
          </NewButton>
        )}
      </div>
    </ToolbarWrapper>
  );
}
